/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useSEOHead } from '../../../hooks/useSEOHead';
import { CORE_BUSINESS } from '../../../lib/coreSchemas';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import { ServiceAreasHero } from '../components/ServiceAreasHero';
import { NearbyAreasSection } from '../components/NearbyAreasSection';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { injectJsonLd, removeJsonLd } from '../../../lib/schemaBuilder';

export default function SeasideServiceAreaPage() {
  useSEOHead({
    title: 'Used Cars Near Seaside OR | No Dealer Fees, Free Delivery | Rockhound Auto',
    description: 'Shop quality used cars near Seaside, Oregon — 15 minutes north in Warrenton. No dealer fees, coastal-inspected vehicles, free delivery to Seaside. Transparent pricing, no pressure.',
    keywords: 'used cars near Seaside Oregon, Seaside used car dealer, no dealer fee cars Seaside OR, used vehicles Seaside Oregon, free delivery used cars Seaside',
    canonicalUrl: `${CORE_BUSINESS.url}/service-areas/seaside`,
    geoRegion: 'US-OR',
    geoPlacename: 'Seaside, Oregon',
    geoPosition: '45.9929;-123.9229',
  });

  const nearbyAreas = [
    { name: 'Warrenton', slug: 'warrenton' },
    { name: 'Astoria', slug: 'astoria' },
    { name: 'Cannon Beach', slug: 'cannon-beach' },
    { name: 'Gearhart', slug: 'gearhart' },
    { name: 'Tillamook', slug: 'tillamook' },
  ];

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Service Areas',
          item: 'https://rockhoundauto.com/service-areas',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Seaside',
          item: 'https://rockhoundauto.com/service-areas/seaside',
        },
      ],
    };

    injectJsonLd('schema-seaside-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-seaside-breadcrumb');
    };
  }, []);

  return (
    <>
      <Breadcrumb
        items={[
          { label: 'Service Areas', path: '/service-areas' },
          { label: 'Seaside, Oregon' },
        ]}
      />

      <ServiceAreasHero
        city="Seaside"
        state="Oregon"
        description="We're 15 minutes north in Warrenton. Every car we sell is ready for summer tourist gridlock, winter storm commutes, and the occasional sand-in-everything situation. Because if it can't survive Seaside parking lots, it doesn't belong on our lot."
        distance="15 minutes from the Turnaround"
      />

      <section aria-label="Why Seaside residents choose Rockhound Auto" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Built for Seaside Life
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We know what your car goes through here — salt spray, sand, fog that won't quit, and parking lots that flood during king tides. Every vehicle is inspected with that reality in mind.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-water-flash-line text-3xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Coastal Durability Tested</h3>
              <p className="text-slate-700">
                We check seals, undercarriage, and electrical systems for salt damage. If it's been sitting in beach parking lots without proper care, we pass.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-3xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Quick Drive from the Prom</h3>
              <p className="text-slate-700">
                15 minutes north on 101. Swing by after hitting Pig 'N Pancake or before a Tillamook Head hike. We'll even meet you at the Cove for a beach test drive.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-truck-line text-3xl text-teal-700"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Free Delivery</h3>
              <p className="text-slate-700">
                Live in Seaside Heights or down by Gearhart? We'll bring the car to you. No need to coordinate rides or take time off work.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">We Know the Turnaround Traffic</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We're not some out-of-town dealer dumping inventory on coast folks. We live here. We know the roads, the weather, and what actually holds up. We've sat in the same summer gridlock, driven through the same winter storms, and dealt with the same sand-in-everything problem.
            </p>
            <p className="text-slate-700 leading-relaxed">
              If we sell you junk, we'll see you at the grocery store. Reputation matters when you're part of the community.
            </p>
          </div>

          {/* Cross-link to dedicated Seaside page */}
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h4 className="font-bold text-slate-900 mb-1">Want the full Seaside buyer guide?</h4>
              <p className="text-slate-700 text-sm">See our in-depth guide to buying used cars in Seaside — local FAQs, popular vehicles, and what coastal buyers need to know.</p>
            </div>
            <Link
              to="/used-cars-seaside-oregon"
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap text-sm"
            >
              Seaside Buyer Guide <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <NearbyAreasSection cityName="Seaside" nearbyAreas={nearbyAreas} />
    </>
  );
}

export { SeasideServiceAreaPage };